import React, { useState } from "react";
import { useUserContext } from "../../../context/UserContext.jsx";
import { createPortal } from "react-dom";
import { apiRequest } from "@/network/apiClient";
import { API_ENDPOINTS } from "@/api/apiEndpoints";
import { toast } from "react-toastify";
import { DropdownInteractionIcon } from "../icons";

const InteractionButtons = ({leadId, refetch}) => {
  const { userId } = useUserContext();
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 🔹 Today's date (YYYY-MM-DD)
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    notes: "",
    purpose: "",
    date: "",
    type: "",
    leadId: leadId || "",
  });

  const purposeOptions = [
    "Promotions/FX Rate",
    "Follow up for document (Order)",
    "Other",
    "Follow up for Next Order (Retention)",
    "Cold Message (Insurance, Financing etc)",
  ];

  const interactionTypes = [
    { type: "Call", icon: <img src="/icons/call.svg" className="size-7" /> },
    { type: "Text/Message", icon: <img src="/icons/whatsapp.svg" className="size-7" /> },
    { type: "E-mail", icon: <img src="/icons/email.svg" className="size-7" /> },
    { type: "Events/In-Person", icon: <img src="/icons/supervisor_account.svg" className="size-7" /> },
  ];

  const handleButtonClick = (type) => {
    setSelectedInteraction(type);
    setIsModalOpen(true);
    setFormData((prevData) => ({
      ...prevData,
      type,
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    setIsModalOpen(false);
    setFormData({ notes: "", purpose: "", date: "" });
  };

  const addInteraction = async () => {
    setIsLoading(true);

    try {
      const response = await apiRequest(
        `${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.ADD_INTERACTION}`,
        "POST",
        formData
      );

      if (refetch) refetch();
      toast.success("Interaction Successfully Added");
      setIsModalOpen(false);
    } catch (error) {
      toast.error(error || "Interaction save unsuccessful");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative group">
      <button
        onClick={() => setIsModalOpen(true)}
        className="text-[#1E293B] block px-4 py-2 text-sm hover:bg-[#F1F5F9] text-center w-full"
      >
        <img src={DropdownInteractionIcon} alt="Interaction" className="inline-block w-6 h-6 mr-2" /> 
        Add Interaction
      </button>

      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg w-1/3 ">
              <div className="border-b border-gray-300 mr-5 mb-6">
                <h2 className="text-xl font-bold mb-2">Add Interaction Details</h2>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Interaction Type:
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="" disabled>Select interaction type</option>
                    {interactionTypes.map((interaction) => (
                      <option key={interaction.type} value={interaction.type}>
                        {interaction.type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1 mt-3">Purpose:</label>
                  <select
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="" disabled>Select a purpose</option>
                    {purposeOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Date:</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                    max={today}  // 🔹 Prevent selecting future dates
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Notes:</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-3 bg-white border-[1px] border-black rounded-3xl hover:bg-gray-100"
                  >
                    <b>Cancel</b>
                  </button>

                  <button
                    type="button"
                    onClick={addInteraction}
                    className={`rounded-3xl px-8 py-3 text-black ${
                      isLoading ? "bg-gray-500" : "bg-yellow-300 hover:bg-yellow-400"
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? "Adding..." : <b>Submit</b>}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default InteractionButtons;